import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProcessRunTaskType, ProcessTemplateTaskType, TaskTypeDirType, TaskTypeStyleType } from '@econtractor/types';
import { UserNameItem } from 'modules/user';
import { useSettings } from 'context/SettingsContext';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import SolarIcon from 'components/icons/SolarIcon';
import TaskTermInterval from '../../../shared/TaskTermInterval';
import TaskTypeChip from '../../../shared/TaskTypeChip';
import { getIncomingProcessTriggers } from '../../processDiagram/helpers/nextTask';
import { formatProcessInterval } from '../../processDiagram/helpers/formatInterval';
import ProcessIntervalLabel from '../../processDiagram/ProcessIntervalLabel';
import ProcessTaskDueDialog, { type ProcessTaskDuePatch } from './ProcessTaskDueDialog';
import ProcessRunTaskStatus from './ProcessRunTaskStatus';
import RowTitle from '../../../shared/RowTitle';

interface ProcessTasksTableRowProps {
    task: ProcessTemplateTaskType;
    tasks: ProcessTemplateTaskType[];
    disabled?: boolean;
    launched?: boolean;
    pruned?: boolean;
    onEdit: (task: ProcessTemplateTaskType) => void;
    onRemove: (task: ProcessTemplateTaskType) => void;
    onDueChange?: (dtId: number, patch: ProcessTaskDuePatch) => void;
    onOpenInstance?: (instanceId: number) => void;
}

const ProcessTasksTableRow = ({
    task,
    tasks,
    disabled,
    launched = false,
    pruned = false,
    onEdit,
    onRemove,
    onDueChange,
    onOpenInstance,
}: ProcessTasksTableRowProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { localSettings } = useSettings();
    const [dueOpen, setDueOpen] = useState(false);
    const dirType = (localSettings?.DIRS?.TaskType as TaskTypeDirType[] | undefined)?.find(
        (item) => item.ID === task.taskType,
    );
    const typeStyle: TaskTypeStyleType | null = task.taslTypeJson?.backgroundColor || task.taslTypeJson?.textColor
        ? task.taslTypeJson
        : dirType?.taslTypeJson ?? null;
    const typeName = task.taskType
        ? tDirName(t, task.taskTypeI18nKey || dirType?.i18nKey, task.taskTypeName || dirType?.taskTypeName)
        : '';
    const triggers = getIncomingProcessTriggers(task, tasks);
    const hasDue = Number(task.dateEnd || 0) > 0;
    const runTask = task as ProcessRunTaskType;
    const instanceId = Number(runTask.instanceId || 0);
    const canOpenInstance = launched && instanceId > 0 && Boolean(onOpenInstance);
    const canOpenEdit = !launched && !disabled;
    const canOpen = canOpenInstance || canOpenEdit;
    const openRow = () => {
        if (canOpenInstance) onOpenInstance?.(instanceId);
        else if (canOpenEdit) onEdit(task);
    };

    return (
        <tr className={pruned ? 'process-task-row--pruned' : undefined}>
            <td className="name">
                <RowTitle
                    name={task.taskName}
                    description={task.taskDescription}
                    onNameClick={canOpen ? openRow : undefined}
                />
            </td>
            <td className="trigger text-break">
                {triggers.length ? (
                    <div className="d-flex flex-column">
                        {triggers.map((item, index) => {
                            const answerLabel = item.answerKey == null
                                ? t('processDir.triggerAny')
                                : (item.answerText || '—');
                            const intervalLabel = formatProcessInterval(item.interval, t, item.intervalAnchor);
                            return (
                                <div
                                    key={`${item.templateId}:${item.dtId}:${item.answerKey ?? 'none'}`}
                                    className={`d-flex flex-column${index > 0 ? ' border-top mt-2 pt-2' : ''}`}
                                    title={`${item.taskName} — ${answerLabel} — ${intervalLabel}`}
                                >
                                    <span className="fw-medium text-body fs-12">{item.taskName}</span>
                                    <span className="fs-10 text-muted">{answerLabel}</span>
                                    <span className="fs-12 text-body">
                                        <ProcessIntervalLabel interval={item.interval} anchor={item.intervalAnchor} />
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ) : hasDue || onDueChange ? (
                    <div className="d-flex flex-column align-items-start gap-1">
                        {hasDue ? (
                            <TaskTermInterval
                                taskId={task.dtId}
                                dateStart={task.dateStart}
                                dateEnd={task.dateEnd}
                                taskStatus={0}
                                withTime={Boolean(task.withTime)}
                                datesOnly
                            />
                        ) : null}
                        {onDueChange ? (
                            <button
                                type="button"
                                className="btn btn-link p-0 align-baseline fs-12"
                                disabled={disabled}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setDueOpen(true);
                                }}
                            >
                                <SolarIcon name={hasDue ? 'pencil' : 'plus'} className="me-1 fs-14" />
                                {hasDue ? t('common:buttons.change') : t('processRuns.btnAddDue')}
                            </button>
                        ) : null}
                        {onDueChange ? (
                            <ProcessTaskDueDialog
                                show={dueOpen}
                                task={task}
                                onHide={() => setDueOpen(false)}
                                onSave={(patch) => {
                                    onDueChange(task.dtId, patch);
                                    setDueOpen(false);
                                }}
                            />
                        ) : null}
                    </div>
                ) : (
                    <span className="text-muted">—</span>
                )}
            </td>
            {launched ? (
                <td className="status">
                    <ProcessRunTaskStatus task={runTask} pruned={pruned} />
                </td>
            ) : null}
            <td className="type">
                <TaskTypeChip
                    name={typeName}
                    icon={task.taskTypeIcon || dirType?.taskTypeIcon}
                    colors={typeStyle}
                    fontSize="0.75rem"
                />
            </td>
            <td className="username">
                {task.executorUser ? (
                    <UserNameItem user={task.executorUser} smallMode />
                ) : (
                    <span className="text-muted">—</span>
                )}
            </td>
            {launched ? null : (
                <td className="action text-end">
                    <button
                        type="button"
                        className="btn btn-sm btn-light-secondary p-1 py-0 me-1"
                        disabled={disabled}
                        onClick={(event) => {
                            event.stopPropagation();
                            onEdit(task);
                        }}
                        aria-label={t('common:buttons.change')}
                    >
                        <SolarIcon name="pencil" className="fs-14" />
                    </button>
                    <Button
                        type="button"
                        size="sm"
                        className="p-1 py-0"
                        variant="light-danger"
                        disabled={disabled}
                        onClick={(event) => {
                            event.stopPropagation();
                            onRemove(task);
                        }}
                        aria-label={t('common:buttons.delete')}
                    >
                        <SolarIcon name="trash3" />
                    </Button>
                </td>
            )}
        </tr>
    );
};

export default ProcessTasksTableRow;
